import React from 'react'
import { useTranslation } from 'react-i18next'
import { OPTIONS } from './constants'
import {
  StepContent,
  Heading,
  Instruction,
  ParagraphForm,
  FormParagraph,
} from './styled'
import { InputField } from '@components/Form/InputField'
import { InlineDropdownField } from '@components/Form/InlineDropdownField'
import { useFormikContext } from 'formik'
import { ApiOnboardingSurveyRequestBody } from 'BackendApi'
import { InlineWidget, useCalendlyEventListener } from 'react-calendly'
import { useGetUserInfo } from '@app/api/getUserInfo'
import { useUpdateUserSurvey } from '@app/api/updateUserSurvey'

interface FormInnerProps {
  currentStep: 1 | 2
  calendlyStep?: boolean
  calendlyUrl?: string
}

export const FormInner: React.FC<FormInnerProps> = ({
  currentStep,
  calendlyStep: calendlyStepProp = false,
  calendlyUrl: calendlyUrlProp = '',
}) => {
  const { t } = useTranslation()
  const { values } = useFormikContext()
  const formValues = values as ApiOnboardingSurveyRequestBody
  const calendlyStep = calendlyStepProp
  const calendlyUrl = calendlyUrlProp

  const { mutate: updateUpdateUserSurveyMutation } = useUpdateUserSurvey()

  const { data: userData } = useGetUserInfo(true)
  const fullName = [userData?.user.firstName, userData?.user.lastName]
    .filter(Boolean)
    .join(' ')
  const email = userData?.user.email || ''
  // Local state to capture scheduled event info
  const [scheduledEventInfo, setScheduledEventInfo] = React.useState<{
    eventUri: string
    inviteeUri: string
  } | null>(null)

  // Attach Calendly event listeners
  useCalendlyEventListener({
    onEventScheduled: (e: {
      data: {
        payload: {
          event: { uri: string }
          invitee: { uri: string }
        }
      }
    }) => {
      const eventUri = e.data.payload.event.uri
      const inviteeUri = e.data.payload.invitee.uri
      setScheduledEventInfo({ eventUri, inviteeUri })
      updateUpdateUserSurveyMutation(undefined, {
        onSuccess: () => {
          console.log('User survey updated after Calendly booking.')
        },
        onError: () => {
          console.log('User survey NOT updated after Calendly booking')
        },
      })
      console.log('Calendly event scheduled:', { eventUri, inviteeUri })
    },
  })

  if (currentStep === 1) {
    return (
      <StepContent>
        <Heading>{t('onboarding.company.heading')}</Heading>
        <Instruction>{t('onboarding.company.instruction')}</Instruction>

        <ParagraphForm>
          <FormParagraph>
            <span style={{ marginRight: '8px' }}>
              {t('onboarding.company.intro.prefix')}
            </span>
            <InlineDropdownField
              name='position'
              placeholder={t('onboarding.company.placeholders.role')}
              options={OPTIONS.roles}
              searchable
            />
            <span style={{ marginLeft: '8px', marginRight: '8px' }}>
              {t('onboarding.company.intro.at')}
            </span>
            <InputField
              type='text'
              name='companyName'
              placeholder={t('onboarding.company.placeholders.companyName')}
              inline
            />
            <span style={{ marginLeft: '8px', marginRight: '8px' }}>
              {t('onboarding.company.intro.a')}
            </span>
            <InlineDropdownField
              name='industry'
              placeholder={t('onboarding.company.placeholders.industry')}
              options={OPTIONS.industries}
              searchable
            />
            <span style={{ marginLeft: '8px' }}>
              {t('onboarding.company.intro.companyWith')}
            </span>
            <InlineDropdownField
              name='capacity'
              placeholder={t('onboarding.company.placeholders.companySize')}
              menuMinWidth='180px'
              options={OPTIONS.companySizes}
            />
            <span style={{ marginLeft: '8px', marginRight: '8px' }}>
              {t('onboarding.company.intro.basedIn')}
            </span>
            <InlineDropdownField
              name='country'
              placeholder={t('onboarding.company.placeholders.country')}
              menuMinWidth='180px'
              options={OPTIONS.countries}
              searchable
            />
            <span style={{ marginLeft: '8px' }}>
              {t('onboarding.company.intro.suffix')}
            </span>
          </FormParagraph>
        </ParagraphForm>
      </StepContent>
    )
  }
  if (!calendlyStep) {
    return (
      <StepContent>
        <Heading>{t('onboarding.goals.heading')}</Heading>
        <Instruction>{t('onboarding.goals.instruction')}</Instruction>

        <ParagraphForm>
          <FormParagraph>
            <span style={{ marginRight: '8px' }}>
              {t('onboarding.company.leadGen.prefix')}
            </span>
            <InlineDropdownField
              name='sourceApp'
              placeholder={t('onboarding.company.placeholders.channels')}
              options={OPTIONS.adPlatforms}
              searchable
            />
            <span style={{ marginLeft: '8px', marginRight: '8px' }}>
              {t('onboarding.company.leadGen.spending')}
            </span>
            <InlineDropdownField
              name='budget'
              placeholder={t('onboarding.company.placeholders.monthlySpend')}
              options={OPTIONS.adSpends}
            />
            <span style={{ marginLeft: '8px', marginRight: '8px' }}>
              {t('onboarding.company.leadGen.perMonth')}
            </span>
            <InlineDropdownField
              name='destinationApp'
              placeholder={t('onboarding.company.placeholders.crmTool')}
              options={OPTIONS.crmTools}
              searchable
            />
            <span style={{ marginLeft: '8px' }}>
              {t('onboarding.company.leadGen.suffix')}
            </span>
          </FormParagraph>

          <FormParagraph>
            <span style={{ marginRight: '8px' }}>
              {t('onboarding.goals.primaryGoal.prefix')}
            </span>
            <InlineDropdownField
              name='goal'
              placeholder={t('onboarding.goals.placeholders.primaryGoal')}
              options={OPTIONS.primaryGoals.filter((o) => {
                const selectedSecondaryGoal = formValues?.secondaryGoal
                // Filter out the secondary goal that was selected
                return o !== selectedSecondaryGoal
              })}
            />
            <span>{t('onboarding.goals.primaryGoal.suffix')}</span>

            <span style={{ marginRight: '8px' }}>
              {t('onboarding.goals.secondaryGoal.prefix')}
            </span>
            <InlineDropdownField
              name='secondaryGoal'
              placeholder={t('onboarding.goals.placeholders.secondaryGoal')}
              options={OPTIONS.secondaryGoals.filter((o) => {
                const selectedGoal = formValues?.goal
                // Filter out the primary goal that was selected
                return o !== selectedGoal
              })}
            />
            <span>{t('onboarding.goals.secondaryGoal.suffix')}</span>
          </FormParagraph>
        </ParagraphForm>
      </StepContent>
    )
  } else {
    return (
      <StepContent>
        <Heading>{t('onboarding.calendly.bookingText')}</Heading>
        {/* Added Headline and Subheadline as requested */}
        <Heading as='h2'>{t('onboarding.calendly.subBookingText')}</Heading>
        <Instruction>
          {t('onboarding.calendly.subHelpBookingText')}
        </Instruction>

        <ParagraphForm>
          {/* Inline Calendly widget replaces popup modal */}
          <InlineWidget
            url={calendlyUrl}
            prefill={{
              name: fullName || undefined,
              email: email || undefined,
              customAnswers: { a1: 'LeadsBridge' },
            }}
            styles={{ height: '1000px', marginTop: '-40px' }}
          />
        </ParagraphForm>
      </StepContent>
    )
  }
}
